// @TODO [CX-663]: clean up this file when workspaces are removed

import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useCurrentSite } from '@hotjar/site';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { FEEDBACK, HEATMAPS, PLAYBACKS, SURVEY_TEMPLATES } from '@hotjar/webapp/constants/routes';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { tokens, Alert, Stack, Text, Button } from '@hotjar/hotsauce';
import NoSurveysImage from '@hotjar/shared-assets/images/surveys/customer-feedback-questions-new.png';
import NoRecordingsImage from '@hotjar/shared-assets/images/empty-recordings-new.png';
import NoHeatmapsImage from '@hotjar/shared-assets/images/empty-heatmaps-new.png';
import NoFeedbackImage from '@hotjar/shared-assets/images/empty-feedback-new.png';
import { navigateToInstaller } from '@hotjar/webapp/domains/activation/routing';
import { useHelpHotjarUrl } from '@hotjar/translations';

export enum EMPTY_STATE_TOOL {
  recordings = 'recordings',
  heatmaps = 'heatmaps',
  surveys = 'surveys',
  feedback = 'feedback',
}

export enum EMPTY_STATE_KIND {
  NO_TC = 'NO_TC',
  ERROR = 'ERROR',
  EMPTY = 'EMPTY',
  INFO = 'INFO',
}

const TOOL_MAP = {
  [EMPTY_STATE_TOOL.recordings]: {
    id: EMPTY_STATE_TOOL.recordings,
    color: tokens.BRAND_COLORS.blossom_200,
    image: NoRecordingsImage,
    url: PLAYBACKS.LIST.path,
  },
  [EMPTY_STATE_TOOL.heatmaps]: {
    id: EMPTY_STATE_TOOL.heatmaps,
    color: tokens.BRAND_COLORS.dusk_400,
    image: NoHeatmapsImage,
    url: HEATMAPS.LIST_CONTINUOUS_CAPTURE.path,
  },
  [EMPTY_STATE_TOOL.surveys]: {
    id: EMPTY_STATE_TOOL.surveys,
    color: tokens.BRAND_COLORS.noon_100,
    image: NoSurveysImage,
    url: SURVEY_TEMPLATES.BASE.path,
  },
  [EMPTY_STATE_TOOL.feedback]: {
    id: EMPTY_STATE_TOOL.feedback,
    color: tokens.BRAND_COLORS.aqua_200,
    image: NoFeedbackImage,
    url: FEEDBACK.CREATE.path,
  },
};

const Illustration = styled.div<{ tool: EMPTY_STATE_TOOL }>`
  padding: ${tokens.spacer.x2};
  text-align: center;
  background-image: url('${({ tool }) => TOOL_MAP[tool].image}');
  background-color: ${({ tool }) => TOOL_MAP[tool].color};
  background-position: center;
  background-repeat: no-repeat;
  background-size: auto 111px;
  height: 120px;
`;

const Heading = styled.span`
  display: block;
  color: ${tokens.color.text_default};
  ${tokens.text('header5')};
`;

const StyledStack = styled(Stack)`
  padding: ${tokens.spacer.x2};
`;

const StyledAlert = styled(Alert)`
  padding: ${tokens.spacer.x1} ${tokens.spacer.x1pt5};
  min-height: unset;
  color: ${tokens.color.text_light};
`;

export interface NewEmptyWidgetProps {
  tool: EMPTY_STATE_TOOL;
  kind: EMPTY_STATE_KIND;
  alertMessage?: string | JSX.Element;
}

export function NewEmptyWidget({ tool, kind, alertMessage }: NewEmptyWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { data: currentSite } = useCurrentSite();
  const { hotjarInstallerOpened, click: trackClickEvent } = useTrack();

  const isIllustrationVisible = ![EMPTY_STATE_KIND.NO_TC, EMPTY_STATE_KIND.ERROR].includes(kind);

  const trackEmptyClick = () => trackClickEvent(`Empty state CTA`, `${tool} widget`, {});

  const trackNoTCClick = () => trackClickEvent(`No TC state CTA`, `${tool} widget`, {});

  const trackWarningStateClick = () => {
    trackClickEvent(`TC Warning state CTA`, `${tool} widget`, {});
  };

  const onNoTCStateCTAClick = () => {
    const shouldRedirectToInstallStep = !!currentSite?.url;
    if (currentSite) {
      trackNoTCClick();

      hotjarInstallerOpened(
        currentSite,
        `${tool} widget`,
        shouldRedirectToInstallStep ? 'install' : 'add-url',
      );

      navigateToInstaller(currentSite);
    }
  };

  return (
    <StyledStack direction="column">
      {isIllustrationVisible ? <Illustration tool={tool} /> : null}
      {kind !== EMPTY_STATE_KIND.NO_TC ? (
        <Heading>{t(`activation:space-overview.empty-state.${TOOL_MAP[tool].id}.title`)}</Heading>
      ) : null}
      <Text textColor="text_light" textType="body2">
        {t(`activation:space-overview.empty-state.${TOOL_MAP[tool].id}.description`)}
      </Text>
      {kind === EMPTY_STATE_KIND.NO_TC ? (
        <>
          <StyledAlert kind="warning">
            {t(`activation:space-overview.empty-state.${TOOL_MAP[tool].id}.install-cta`)}
          </StyledAlert>
          <div>
            <Button kind="secondary" onClick={onNoTCStateCTAClick}>
              {t(`activation:dashboard.install-hotjar`)}
            </Button>
          </div>
        </>
      ) : null}
      {kind === EMPTY_STATE_KIND.INFO ? (
        <StyledAlert kind="info">
          {alertMessage ||
            t(`activation:space-overview.empty-state.${TOOL_MAP[tool].id}.tc-warning`)}
        </StyledAlert>
      ) : null}
      {kind === EMPTY_STATE_KIND.ERROR ? (
        <>
          <StyledAlert kind="warning">
            {t(`activation:space-overview.empty-state.${TOOL_MAP[tool].id}.tc-warning`)}
          </StyledAlert>
          <div>
            <Button
              as="a"
              href={`${helpHotjarUrl}/articles/115009509288-Why-is-My-Tracking-Code-Not-Verifying-#:~:text=The%20most%20common%20reason%20why,try%20verifying%20the%20installation%20again`}
              isExternal
              icon="open_new_window"
              kind="secondary"
              onClick={trackWarningStateClick}
            >
              {t(`activation:space-overview.empty-state.tc-warning-cta`)}
            </Button>
          </div>
        </>
      ) : null}
      {kind === EMPTY_STATE_KIND.EMPTY && currentSite?.id ? (
        <div data-testid="cta">
          <Button
            onClick={trackEmptyClick}
            href={getRoute(TOOL_MAP[tool].url as string, { siteId: currentSite?.id as number })}
            as="a"
            kind="secondary"
          >
            {t(`activation:space-overview.empty-state.${TOOL_MAP[tool].id}.cta`)}
          </Button>
        </div>
      ) : null}
    </StyledStack>
  );
}
